import * as React from "react";
import { logger } from "bernie-core";

import { withLocalization } from "@shared-ui/localization-context";

import { inject, observer } from "mobx-react";

import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { WizardClickstreamContext } from "components/flexComponents/PageLevelProviders/WizardContextProvider/WizardClickstreamContext";
import {
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
  getStandaloneLOBModulesInPackages,
  getWizardImageOverride,
  getAboveNonWizardFormChildren,
  getBelowNonWizardFormChildren,
} from "components/shared/StorefrontWizard/StorefrontWizardUtils";

import { getTracker } from "utils/TrackingUtils";
import { PackagesStandaloneWizardModulesContext } from "components/flexComponents/PageLevelProviders/WizardContextProvider/PackagesStandaloneWizardModulesContext";
import { StorefrontWizardRegionBEXProps, StorefrontWizardRegionBEXFTNodeOptions } from "./typings";

import { StorefrontWizardRegionBEXView } from "./components/StorefrontWizardRegionBEXView";
import { StorefrontWizardRegionBEXViewV2 } from "./components/StorefrontWizardRegionBEXViewV2";
import {
  isVariantEnabled,
  getExperimentBucketFromExperimentsStore,
  Experiment,
} from "src/components/utility/experiment";
import { normalizeOptionsData, hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";
import { ExtendedFTNode } from "src/typings/flexFramework/FlexComponent";
import { WizardBackgroundImageOverrideProps } from "../typings";

@withLocalization
@logger("StorefrontWizardRegionBEX")
@observer
@inject("wizardState", "analytics", "context", "experiment", "flexModuleModelStore", "compositionStore")
export class StorefrontWizardRegionBEX extends React.Component<StorefrontWizardRegionBEXProps> {
  public render() {
    const {
      context,
      wizardState,
      analytics,
      logger: loggerInstance,
      l10n,
      blossomComponent,
      flexTemplateNode,
      flexModuleModelStore,
      experiment,
      compositionStore,
    } = this.props;

    if (!blossomComponent) {
      return null;
    }

    //default to false if device information is not available
    wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

    const options = normalizeOptionsData(
      flexTemplateNode,
      flexModuleModelStore.getModel(flexTemplateNode.id)
    ) as StorefrontWizardRegionBEXFTNodeOptions;

    if (!options || !hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    if (options.defaultLOB) {
      wizardState.globalWizardState.config.navigation.defaultLob = options.defaultLOB;
      wizardState.globalWizardState.config.navigation.activeLob = options.defaultLOB;
    }

    const children = blossomComponent.renderChildren(loggerInstance, l10n, context);

    if (children.length < 1) {
      return null;
    }

    const backgroundImageOverride: WizardBackgroundImageOverrideProps | undefined = getWizardImageOverride(
      children,
      context,
      flexModuleModelStore
    );

    const wizardLOBs = getWizardLOBFlexComponents(flexTemplateNode.childNodes as ExtendedFTNode[]);

    const wizardFormChildren = getWizardLOBFormChildren(children);

    const aboveOtherFormChildren = getAboveNonWizardFormChildren(children, flexModuleModelStore);

    const belowOtherFormChildren = getBelowNonWizardFormChildren(children, flexModuleModelStore);

    wizardState.globalWizardState.config.heroImageSrc =
      backgroundImageOverride?.src ?? options.backgroundImage ?? wizardState.globalWizardState.config.heroImageSrc;
    const { heroImageSrc } = wizardState.globalWizardState.config;

    const heroImageColor = backgroundImageOverride?.color ?? options.backgroundImageColor;

    const lobNameMap = {
      "wizard-hotel-pwa-v2": "_Hotels",
      "wizard-flight-pwa": "_Flights",
      "wizard-package-pwa": "_Packages",
      "wizard-car-pwa": "_Cars",
      "wizard-lx-pwa": "_Lx",
      "wizard-cruise-pwa": "_Cruise",
      "wizard-thirdPartyPackage-pwa": "_ThirdPartyPackage",
      "wizard-externalLinkTab-pwa": "_ExternalLinkTab",
    };

    const trackOnChangeTab = (tabName: string, rfrrId?: string) => {
      const moduleName = options.flexComponentName === "" ? "SFWizardBEX" : options.flexComponentName;
      const tracker = getTracker(wizardState.canTrack, analytics, moduleName);
      const lobName = lobNameMap[tabName];
      const rfrr = `LOB_Selection_Occured${lobName ?? ""}`;
      tracker.trackEvent(`${rfrr}${rfrrId ?? ""}`);
    };

    const {
      title,
      cdgTreatment,
      titleFromPageHeader,
      subTitleFromPageSubHeader,
      innerTitle,
      cobrandImageUrl,
    } = options;

    const shouldDisplayPackageWizardMultiLOB = wizardState.globalWizardState.config.includeSingleLOBsWithinPackages;
    const wizardModules = shouldDisplayPackageWizardMultiLOB ? getStandaloneLOBModulesInPackages(children) : [];

    wizardState.globalWizardState.config.cobrandImageUrl = cobrandImageUrl;

    const isBrandRefreshJuneEnabled = isVariantEnabled(context, Experiment.EXPEDIA_BRAND_REFRESH_JUNE);
    const isWizardClickstreamEnabled =
      getExperimentBucketFromExperimentsStore(experiment, Experiment.WIZARD_CLICKSTREAM) > 0;
    const wizardClickstream = {
      isEnabled: isWizardClickstreamEnabled,
    };

    const heading = titleFromPageHeader ? compositionStore.composition!.pageHeading : title;
    const subHeading = subTitleFromPageSubHeader ? compositionStore.composition!.pageSubHeadline : innerTitle;

    const wizardViewProps = {
      heroImageSrc,
      context,
      trackOnChangeTab,
      wizardFormChildren,
      wizardLOBs,
      wizardState,
      heroImageColor,
      cdgTreatment,
      heading,
      subHeading,
    };

    /**
     *
     * Note for modules below this region:
     * Modules not part of this region do not align to the form content out of the box
     * Setting the flex manager attribute brandModulePadding to bex_padding puts a spacing__twelve on the components below this region
     * This aligns them with the form content within the card on this wizard
     * On mobile, there is no card, and this same attribute puts spacing__six on these components
     */
    return (
      <StorefrontWizardRegionTypesContext.Provider value={StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_BEX}>
        <PackagesStandaloneWizardModulesContext.Provider value={wizardModules}>
          <WizardClickstreamContext.Provider value={wizardClickstream}>
            {isBrandRefreshJuneEnabled && (
              <StorefrontWizardRegionBEXViewV2
                {...wizardViewProps}
                aboveOtherFormChildren={aboveOtherFormChildren}
                belowOtherFormChildren={belowOtherFormChildren}
              />
            )}

            {!isBrandRefreshJuneEnabled && (
              <StorefrontWizardRegionBEXView
                {...wizardViewProps}
                otherFormChildren={aboveOtherFormChildren.concat(belowOtherFormChildren)}
              />
            )}
          </WizardClickstreamContext.Provider>
        </PackagesStandaloneWizardModulesContext.Provider>
      </StorefrontWizardRegionTypesContext.Provider>
    );
  }
}

export default StorefrontWizardRegionBEX;
